import { supabase } from "@/integrations/supabase/client";

export type ActivityAction = 
  | 'create' | 'update' | 'delete' | 'view'
  | 'approve' | 'reject' | 'sync' | 'login' | 'logout'
  | 'bulk_import' | 'bulk_export' | 'bulk_update' | 'bulk_visibility_update'
  | 'dintero_checkout' | 'dintero_capture' | 'dintero_refund' | 'dintero_webhook'
  | 'soap_sendArticle' | 'soap_getOrders' | 'soap_updateOrderStatus' | 'soap_creditOrder'
  | 'webshop_publish' | 'webshop_unpublish';

export type EntityType = 
  | 'article' | 'order' | 'customer' | 'discount' 
  | 'webshop' | 'credit_application' | 'settings'
  | 'payment' | 'soap_call';

interface LogActivityParams {
  action: ActivityAction;
  entityType: EntityType;
  entityId?: string;
  details?: Record<string, any>;
}

export async function logActivity({
  action,
  entityType,
  entityId,
  details,
}: LogActivityParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.from('activity_logs').insert({
      user_id: user?.id,
      action,
      entity_type: entityType,
      entity_id: entityId,
      details,
      ip_address: null, // Could be fetched from a service
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    console.error('Failed to log activity:', error);
    // Don't throw - logging should not break the main flow
  }
}

// Specialized logging functions for integrations
export async function logDinteroPayment(
  webshopId: number,
  orderId: number,
  action: 'dintero_checkout' | 'dintero_capture' | 'dintero_refund' | 'dintero_webhook',
  amount: number,
  success: boolean,
  errorMessage?: string
) {
  await logActivity({
    action,
    entityType: 'payment',
    entityId: orderId.toString(),
    details: { 
      webshopId, 
      amount, 
      success,
      errorMessage,
      timestamp: new Date().toISOString()
    }
  });
}

export async function logPCKasseSOAP(
  webshopId: number,
  method: string,
  success: boolean,
  details: any
) {
  const action = `soap_${method}` as ActivityAction;
  await logActivity({
    action,
    entityType: 'soap_call',
    entityId: webshopId.toString(),
    details: { 
      ...details, 
      success,
      timestamp: new Date().toISOString()
    }
  });
}

export async function logWebshopPublication(
  webshopId: number,
  published: boolean
) {
  await logActivity({
    action: published ? 'webshop_publish' : 'webshop_unpublish',
    entityType: 'webshop',
    entityId: webshopId.toString(),
    details: { 
      published,
      timestamp: new Date().toISOString()
    }
  });
}
